import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { MapPin, Maximize2, Minimize2 } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as devicesApi from "../../api/devices";
import { MappaDevice, SERVIZIO_PRECREDIT_COLORE, SERVIZIO_PRECREDIT_LABELS } from "../../api/devices";
import { ClienteLite } from "../../api/users";
import { ClientiMultiSelect } from "../ClientiMultiSelect";
import { useCountUp } from "../../hooks/useCountUp";
import { buildPuntoIcon } from "./mapIcons";
import { CorniceMappa } from "./CorniceMappa";
import { ExportButton } from "./ExportButton";
import { etichettaMatricola } from "../../utils/matricola";

const CENTRO_ITALIA: [number, number] = [42.5, 12.5];
const ZOOM_ITALIA = 5.5;

/**
 * I due colori del marchio, presi dal file vettoriale del logo
 * (`public/winkoncash.svg`): `.cls-1` il verde, `.cls-3` l'arancio del pallino
 * sopra la "i". Non dalla guida di visual identity, che ne dà versioni
 * leggermente diverse: questi sono quelli realmente disegnati nel logo che
 * compare qui accanto, e devono combaciare con esso.
 *
 * Il verde arriva da `SERVIZIO_PRECREDIT_COLORE` e non è ridefinito qui: è lo
 * stesso colore della tessera nel riepilogo Dispositivi, e due punti dell'app
 * che parlano dello stesso servizio non devono poter divergere.
 */
const VERDE = SERVIZIO_PRECREDIT_COLORE.wink_on_cash;
const ARANCIO = "#f3b149";

/**
 * Comando "schermo intero", sempre visibile sopra la mappa.
 *
 * `z-[1001]` è un valore esplicito e non un token: sta appena sopra i controlli
 * di Leaflet, che stanno a 1000, e deliberatamente SOTTO `z-header` (1040), così
 * scorrendo la pagina la barra di navigazione lo copre come copre la mappa —
 * vedi la nota sulla scala z in tailwind.config.
 */
function BottoneSchermoIntero({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Apri a schermo intero"
      aria-label="Apri la mappa a schermo intero"
      className="absolute right-3 top-3 z-[1001] rounded-lg border border-line bg-white p-2 text-ink/60 shadow-card transition hover:text-ink hover:shadow-lift"
    >
      <Maximize2 className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

/**
 * Mappa dei device Wink On Cash attivi, posizionati sulla sede della relazione
 * corrente.
 *
 * ## Perché accanto alla mappa c'è un conteggio degli esclusi
 *
 * Un device senza sede collegata, o con una sede mai geocodificata, non ha
 * niente da disegnare: è la stessa regola degli altri livelli della Mappa. Ma
 * senza dirlo il numero dei pin sembrerebbe il numero dei device Wink On Cash,
 * e non lo è — nei dati di sviluppo sono 8 pin su 71 device. Chi confronta con
 * la tessera del riepilogo Dispositivi vedrebbe due numeri veri che si
 * contraddicono, senza modo di capire da dove nasce la differenza.
 *
 * Le due cause restano separate perché sono due lavori diversi: collegare la
 * sede è anagrafica, geocodificarla è un'altra cosa.
 */
export function MappaWinkOnCash() {
  const { showToast } = useToast();
  const [dati, setDati] = useState<MappaDevice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [clienti, setClienti] = useState<ClienteLite[]>([]);

  useEffect(() => {
    let annullato = false;
    setIsLoading(true);
    devicesApi
      .getMappaDevice(clienti.map((c) => c.id))
      .then((d) => {
        if (!annullato) setDati(d);
      })
      .catch((err) => {
        if (annullato) return;
        // Come il riepilogo del parco: un blocco che sparisce in silenzio non si
        // distingue da una funzione che non esiste.
        setDati(null);
        showToast(getErrorMessage(err, "Impossibile caricare la mappa Wink On Cash."), "error");
      })
      .finally(() => {
        if (!annullato) setIsLoading(false);
      });
    return () => {
      annullato = true;
    };
  }, [clienti, showToast]);

  const punti = dati?.punti ?? [];
  const esclusi = dati?.esclusi;
  const totaleEsclusi = (esclusi?.senza_sede ?? 0) + (esclusi?.sede_non_geolocalizzata ?? 0);

  // Si contano le RELAZIONI, non i pin: una cassaforte condivisa da due clienti
  // Wink On Cash serve due punti vendita, e il pin resta comunque uno perché
  // duplicarlo sulla stessa coordinata non direbbe niente di più. È il contrario
  // della tessera nel riepilogo Dispositivi, che conta device di proposito —
  // vedi la nota estesa sull'endpoint in `routes/devices.ts`.
  const relazioni = dati?.relazioni_disegnate ?? 0;
  const animato = useCountUp(relazioni);

  /**
   * L'indirizzo dell'elenco Dispositivi filtrato su una delle due cause di
   * esclusione.
   *
   * Porta con sé TUTTO ciò che restringe la mappa, non solo la causa: il
   * servizio, perché il widget guarda solo Wink On Cash e senza quel parametro
   * l'elenco mostrerebbe anche i device di altri servizi (e includerebbe gli
   * accessori, che `?servizio=` ora esclude); e i clienti selezionati, perché un
   * elenco che parla di tutto il parco mentre la mappa ne mostra uno solo
   * risponderebbe a una domanda che nessuno ha fatto.
   *
   * `cliente_id` si ripete una volta per cliente — è la forma che il backend
   * già si aspetta, la stessa usata dall'export qui accanto.
   */
  const linkEsclusi = (causa: "senza_sede" | "sede_non_geolocalizzata") => {
    const params = new URLSearchParams({ servizio: "wink_on_cash", mappa: causa });
    for (const c of clienti) params.append("cliente_id", c.id);
    return `/devices?${params.toString()}`;
  };

  const [schermoIntero, setSchermoIntero] = useState(false);

  useEffect(() => {
    if (!schermoIntero) return;
    const chiudiConEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSchermoIntero(false);
    };
    window.addEventListener("keydown", chiudiConEsc);
    // La pagina sotto non deve scorrere mentre si trascina la mappa: senza,
    // il gesto di pan finisce per far scorrere Statistiche dietro l'overlay.
    const overflowPrecedente = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", chiudiConEsc);
      document.body.style.overflow = overflowPrecedente;
    };
  }, [schermoIntero]);

  /**
   * Il contenuto della mappa, scritto una volta e reso a due altezze diverse —
   * nel riquadro della pagina e a schermo intero. Duplicarlo significherebbe
   * due elenchi di marker e due popup da tenere allineati, che è il modo in cui
   * la versione grande smette di mostrare quello che mostra la piccola.
   */
  const corpoMappa = (altezza: string) => {
    if (isLoading && punti.length === 0) {
      return (
        <div style={{ height: altezza }} className="flex items-center justify-center text-sm text-ink/40">
          Caricamento…
        </div>
      );
    }

    if (punti.length === 0) {
      return (
        <div
          style={{ height: altezza }}
          className="flex flex-col items-center justify-center gap-2 px-6 text-center"
        >
          <MapPin className="h-6 w-6 text-ink/25" aria-hidden="true" />
          <p className="text-sm text-ink/50">
            {clienti.length > 0
              ? "Nessun punto vendita Wink On Cash geolocalizzato per i clienti selezionati."
              : "Nessun punto vendita Wink On Cash geolocalizzato."}
          </p>
          {totaleEsclusi > 0 && (
            <p className="text-xs text-ink/40">
              {totaleEsclusi} {totaleEsclusi === 1 ? "rientra" : "rientrano"} nel filtro ma non
              {totaleEsclusi === 1 ? " ha" : " hanno"} una posizione da disegnare.
            </p>
          )}
        </div>
      );
    }

    return (
      <MapContainer center={CENTRO_ITALIA} zoom={ZOOM_ITALIA} scrollWheelZoom style={{ height: altezza, width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {punti.map((p) => (
          <Marker
            key={p.device_id}
            position={[p.lat, p.lng]}
            icon={buildPuntoIcon(VERDE, p.precisione === "comune", ARANCIO)}
          >
            <Popup>
              <div className="flex flex-col gap-0.5 text-xs">
                <span className="text-sm font-bold text-ink">{etichettaMatricola(p.matricola)}</span>
                {p.cliente && <span className="text-ink/70">{p.cliente}</span>}
                {(p.sede_nome || p.sede_citta) && (
                  <span className="text-ink/50">{[p.sede_nome, p.sede_citta].filter(Boolean).join(" · ")}</span>
                )}
                {p.relazioni_servite > 1 && (
                  <span className="font-medium text-ink/70">
                    Cassaforte condivisa: serve {p.relazioni_servite} punti vendita Wink On Cash.
                  </span>
                )}
                {p.precisione === "comune" && (
                  <span className="text-ink/45">Posizione approssimata al centro del comune.</span>
                )}
                <Link to={`/devices/${p.device_id}`} className="mt-1 font-semibold text-primary hover:underline">
                  Apri la scheda
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    );
  };

  return (
    <section className="mb-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">
          {SERVIZIO_PRECREDIT_LABELS.wink_on_cash} — dove sono
        </h2>
        {/* Esporta quello che si sta guardando: i clienti selezionati viaggiano
            come filtro, altrimenti il foglio direbbe una cosa diversa dalla
            mappa da cui è partito. */}
        <ExportButton
          endpoint="/devices/mappa/export"
          filename="wink-on-cash-mappa.xlsx"
          extraParams={{ cliente_id: clienti.map((c) => c.id) }}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        <div className="flex flex-col gap-4">
          {/* --- Conteggio ------------------------------------------------- */}
          <div
            className="flex flex-col rounded-card border border-line border-l-4 bg-white p-4 shadow-card"
            style={{ borderLeftColor: VERDE }}
          >
            {/* Numero e logo sulla stessa riga, centrati fra loro.
                Il numero non si stringe (`shrink-0`) e il logo prende tutto lo
                spazio che avanza fino al bordo: `flex-1` con `min-w-0`, perché
                senza quest'ultimo un'immagine in un contenitore flex non scende
                mai sotto la propria larghezza intrinseca e sfonderebbe la card.
                Essendo un wordmark 780×110, riempire la larghezza gli dà da sé
                l'altezza giusta accanto a un numero di 44px.

                Il logo resta un <img> e non è iniettato nel DOM come fa `Logo`:
                quel componente usa il fetch+inject solo perché il logo di Wink
                on Support contiene testo e deve ereditare Montserrat dalla
                pagina. Questo è fatto di soli tracciati. */}
            <div className="flex items-center gap-3">
              <span
                className="shrink-0 text-[44px] font-bold leading-none tabular-nums"
                style={{ color: VERDE }}
              >
                {animato}
              </span>
              <img
                src="/winkoncash.svg"
                alt="Wink On Cash"
                className="h-auto max-h-[44px] w-full min-w-0 flex-1 object-contain object-right"
              />
            </div>
            <span className="mt-0.5 text-xs text-ink/50">
              {relazioni === 1 ? "Punto vendita sulla mappa" : "Punti vendita sulla mappa"}
              {punti.length !== relazioni && (
                <span className="text-ink/40">
                  {" "}
                  · {punti.length} {punti.length === 1 ? "cassaforte" : "casseforti"}
                </span>
              )}
            </span>

            {totaleEsclusi > 0 && (
              <div className="mt-3 border-t border-line pt-3 text-xs text-ink/50">
                {/* Anche qui relazioni, non device: sommate a quelle disegnate
                    danno il totale delle relazioni Wink On Cash correnti, e chi
                    somma i numeri a schermo non trova un buco da spiegare. */}
                <p className="font-medium text-ink/60">
                  {totaleEsclusi} {totaleEsclusi === 1 ? "punto vendita" : "punti vendita"} non sulla mappa
                </p>
                {/* Le due cause sono cliccabili e portano all'elenco Dispositivi
                    filtrato su quella sola causa: sono due lavori diversi —
                    collegare la sede è anagrafica, geocodificarla è un'altra
                    cosa — e chi apre la lista sta per farne uno dei due, non
                    entrambi. Stesso meccanismo di drill-down delle tessere per
                    servizio: un query param che il backend traduce in filtro.

                    Il numero di righe che si apre PUÒ essere minore di quello
                    scritto qui: il widget conta punti vendita (relazioni),
                    l'elenco mostra casseforti (device), e una condivisa da due
                    clienti vale 2 qui e 1 riga là. È la stessa divergenza già
                    dichiarata sopra per i pin, accettata per lo stesso motivo:
                    sono due domande diverse. Il `title` lo dice a chi passa
                    sopra il link, invece di lasciarlo scoprire contando. */}
                <ul className="mt-1 space-y-0.5">
                  {esclusi!.senza_sede > 0 && (
                    <li>
                      <Link
                        to={linkEsclusi("senza_sede")}
                        title="Apri l'elenco delle casseforti con questa causa. Il numero di righe può essere minore: qui si contano i punti vendita, là le casseforti."
                        className="text-primary hover:underline"
                      >
                        {esclusi!.senza_sede} senza sede collegata
                      </Link>
                    </li>
                  )}
                  {esclusi!.sede_non_geolocalizzata > 0 && (
                    <li>
                      <Link
                        to={linkEsclusi("sede_non_geolocalizzata")}
                        title="Apri l'elenco delle casseforti con questa causa. Il numero di righe può essere minore: qui si contano i punti vendita, là le casseforti."
                        className="text-primary hover:underline"
                      >
                        {esclusi!.sede_non_geolocalizzata} con sede non geolocalizzata
                      </Link>
                    </li>
                  )}
                </ul>
                <Link
                  to="/devices?servizio=wink_on_cash"
                  className="mt-2 inline-block font-semibold text-primary hover:underline"
                >
                  Vedi in Dispositivi
                </Link>
              </div>
            )}
          </div>

          {/* --- Filtro cliente -------------------------------------------- */}
          <div className="rounded-card border border-line bg-white p-4 shadow-card">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink/45">Filtra per cliente</p>
            <ClientiMultiSelect selected={clienti} onChange={setClienti} />
            {clienti.length === 0 && (
              <p className="mt-2 text-xs text-ink/40">Nessun cliente selezionato: la mappa mostra tutti.</p>
            )}
          </div>
        </div>

        {/* --- Mappa, dentro la cornice ------------------------------------- */}
        <CorniceMappa sopra={<BottoneSchermoIntero onClick={() => setSchermoIntero(true)} />}>
          {corpoMappa("420px")}
        </CorniceMappa>
      </div>

      {/* --- Schermo intero -------------------------------------------------
          Overlay a pagina piena invece del componente `Modal`: quello ha una
          barra col titolo, il padding del corpo e lo scorrimento verticale,
          tutte cose che a una mappa a schermo intero tolgono spazio senza
          darle niente. Riusa però il token `z-modal`, così la regola su chi
          sta sopra a chi resta dichiarata in un punto solo (tailwind.config).

          Il contenuto è lo STESSO `corpoMappa` del riquadro piccolo: Leaflet si
          reinizializza cambiando genitore — quindi zoom e centratura ripartono,
          che aprendo a schermo intero è anche l'attesa — ma marker, popup e
          filtro cliente vengono dallo stato del componente e restano quelli. */}
      {schermoIntero && (
        <div className="fixed inset-0 z-modal flex flex-col bg-white">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <img src="/winkoncash.svg" alt="Wink On Cash" className="h-4 w-auto shrink-0" />
              <span className="truncate text-sm text-ink/60">
                <span className="font-bold text-ink">{relazioni}</span>{" "}
                {relazioni === 1 ? "punto vendita" : "punti vendita"} sulla mappa
                {clienti.length > 0 && ` · ${clienti.length} ${clienti.length === 1 ? "cliente" : "clienti"} filtrati`}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setSchermoIntero(false)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-sm text-ink/70 transition hover:bg-black/5 hover:text-ink"
            >
              <Minimize2 className="h-4 w-4" aria-hidden="true" />
              Chiudi
            </button>
          </div>
          <div className="min-h-0 flex-1">{corpoMappa("100%")}</div>
        </div>
      )}
    </section>
  );
}
